import db from './db';
import api from '../connections/api';
import { getStoreName, isPlannerProjectActionUrl } from './endpointMapper';
import { resolvePlannerActionPayload } from './plannerProjectsOffline';
import endpointToStoreMap from './endpointMapper';
import routes_api from '../connections/routes_api';
import { conflictResolver } from './conflictResolver';
import { topologicalSort, getRelationshipFields, RELATIONSHIP_FIELDS } from './dependencyGraph';
import { idMapper } from './idMapper';

function resourceUrl(url, itemId) {
  if (!url) return url;
  const qIndex = url.indexOf('?');
  const query = qIndex >= 0 ? url.slice(qIndex) : '';
  let path = qIndex >= 0 ? url.slice(0, qIndex) : url;
  if (!path.endsWith('/')) path = `${path}/`;
  if (itemId == null || itemId === '') return `${path}${query}`;
  const idStr = String(itemId);
  if (path.replace(/\/+$/, '').endsWith(`/${idStr}`)) return `${path}${query}`;
  return `${path}${idStr}/${query}`;
}

class SyncService {
  constructor() {
    this.isSyncing = false;
    this.syncListeners = [];
    this.maxRetries = 5;
    this.retryDelay = 1000;
    this.pendingConflicts = new Map(); // Map<itemId, {item, localItem, serverItem}>
  }

  // ##### INITIALIZE - Start monitoring online/offline
  initialize() {
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());

    if (navigator.onLine) {
      setTimeout(() => this.sync(), 2000);
    }
  }

  // ##### HANDLE ONLINE - Start sync when back online
  handleOnline() {
    console.log('Connection restored - starting sync...');
    this.sync();
  }

  // ##### HANDLE OFFLINE - Pause sync
  handleOffline() {
    console.log('Connection lost - sync paused');
    this.notifyListeners({ isOnline: false });
  }

  // ##### SYNC - Process sync queue
  async sync() {
    if (this.isSyncing || !navigator.onLine) {
      return;
    }

    this.isSyncing = true;
    this.notifyListeners({ isSyncing: true, isOnline: true });

    try {
      const pendingItems = await db.sync_queue
        .where('status')
        .equals('pending')
        .toArray();

      if (pendingItems.length === 0) {
        this.isSyncing = false;
        this.notifyListeners({ isSyncing: false, pendingCount: 0 });
        return;
      }

      // ##### ORDER BY DEPENDENCIES
      // Group items by store and order stores by dependencies
      const itemsByStore = {};
      for (const item of pendingItems) {
        const storeName = getStoreName(item.url);
        if (storeName) {
          if (!itemsByStore[storeName]) {
            itemsByStore[storeName] = [];
          }
          itemsByStore[storeName].push(item);
        }
      }

      // Sort stores by dependencies (topological sort)
      const sortedStores = topologicalSort(Object.keys(itemsByStore));
      
      // Build ordered items list (respecting dependencies, then timestamp within same store)
      const orderedItems = [];
      for (const storeName of sortedStores) {
        const storeItems = itemsByStore[storeName]
          .filter(item => item.conflict_status !== 'detected') // Skip conflicts
          .sort((a, b) => a.timestamp - b.timestamp); // Sort by timestamp within store
        orderedItems.push(...storeItems);
      }

      // Add items without store mapping at the end (sorted by timestamp)
      const unmappedItems = pendingItems.filter(item => !getStoreName(item.url));
      unmappedItems.sort((a, b) => a.timestamp - b.timestamp);
      orderedItems.push(...unmappedItems);

      this.notifyListeners({ 
        isSyncing: true, 
        pendingCount: orderedItems.length,
        currentItem: 0,
        totalItems: orderedItems.length
      });

      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < orderedItems.length; i++) {
        const item = orderedItems[i];

        this.notifyListeners({ 
          isSyncing: true, 
          currentItem: i + 1,
          totalItems: orderedItems.length
        });

        try {
          await this.syncItem(item);
          successCount++;
        } catch (error) {
          if (error.isConflict) {
            // Conflict detected, skip for now (will be handled by user)
            continue;
          }
          console.error(`Sync failed for item ${item.id}:`, error.message);
          errorCount++;
          await this.handleSyncError(item, error);
        }
      }

      this.notifyListeners({ 
        isSyncing: false, 
        successCount,
        errorCount,
        pendingCount: await db.sync_queue.where('status').equals('pending').count(),
        retryingCount: await db.sync_queue.where('status').equals('retrying').count(),
        failedCount: await db.sync_queue.where('status').equals('failed').count()
      });

    } catch (error) {
      console.error('Sync error:', error);
      this.notifyListeners({ isSyncing: false, error: error.message });
    } finally {
      this.isSyncing = false;
    }
  }

  // ##### SYNC ITEM - Sync single item
  async syncItem(item) {
    const { method, url, data } = item;
    let response;

    // ##### CONFLICT DETECTION (for PUT/PATCH)
    if ((method === 'PUT' || method === 'PATCH') && data.id && !data.id.toString().startsWith('temp_')) {
      try {
        const itemId = this.extractIdFromUrl(url) || data.id;
        const storeName = getStoreName(url);
        
        if (storeName) {
          // Buscar item local do IndexedDB
          const localItem = await db[storeName].get(itemId);
          
          if (localItem && localItem.updated_at) {
            // Buscar item atual do servidor
            const urlWithId = resourceUrl(url, itemId);
            const serverResponse = await api.get(urlWithId);
            const serverItem = serverResponse.data;
            
            // Detectar conflito
            if (conflictResolver.detectConflict(localItem, serverItem)) {
              console.warn(`⚠️ Conflict detected for ${method} ${urlWithId}`);
              
              // Marcar conflito na fila
              await db.sync_queue.update(item.id, {
                conflict_status: 'detected',
                conflict_detected_at: Date.now()
              });
              
              // Armazenar conflito pendente
              this.pendingConflicts.set(item.id, {
                item,
                localItem,
                serverItem,
                storeName
              });
              
              // Notificar listeners
              const itemName = this.getItemDisplayName(localItem, storeName);
              const conflictCount = await db.sync_queue.where('conflict_status').equals('detected').count();
              this.notifyListeners({
                conflictDetected: true,
                conflictItemId: item.id,
                conflictItemName: itemName,
                conflictUrl: urlWithId,
                conflictCount
              });
              
              // Lançar erro especial para pausar sync deste item
              const conflictError = new Error('Conflict detected');
              conflictError.isConflict = true;
              throw conflictError;
            }
          }
        }
      } catch (error) {
        if (error.isConflict) {
          throw error;
        }
        console.warn('Failed to check conflict (proceeding anyway):', error.message);
      }
    }

    // ##### RESOLVE RELATIONSHIPS BEFORE SYNC
    const storeName = getStoreName(url);
    let resolvedData = { ...data };
    
    if (storeName) {
      // Resolve all temporary IDs in relationships
      resolvedData = await idMapper.resolveAllTempIds(resolvedData, storeName);
      
      // Resolve specific relationship fields
      const relationshipFields = getRelationshipFields(storeName);
      if (relationshipFields.length > 0) {
        resolvedData = await idMapper.resolveRelationships(resolvedData, storeName, relationshipFields);
      }
    }

    if (method === 'POST') {
      let payload = { ...resolvedData };
      if (isPlannerProjectActionUrl(url)) {
        payload = await resolvePlannerActionPayload(payload);
      } else {
        delete payload.id;
        delete payload.synced;
        delete payload.updated_at;
        delete payload.local_updated_at;
      }
      response = await api.post(url, payload);
    } else if (method === 'PUT' || method === 'PATCH') {
      // For PUT/PATCH, try to resolve temp ID to real ID first
      let itemId = this.extractIdFromUrl(url) || resolvedData.id;
      
      // If itemId is temporary, try to get real ID from mapping
      if (itemId && itemId.toString().startsWith('temp_')) {
        const realId = await idMapper.getRealId(itemId);
        if (realId) {
          itemId = realId;
        } else {
          throw new Error(`Cannot sync: temporary ID ${itemId} not yet resolved. Dependencies must be synced first.`);
        }
      }
      
      if (!itemId) {
        throw new Error('Cannot sync: ID not found');
      }
      
      const payload = { ...resolvedData };
      delete payload.id;
      delete payload.synced;
      delete payload.updated_at;
      delete payload.local_updated_at;
      const urlWithId = resourceUrl(url, itemId);
      response = await api.put(urlWithId, payload);
    } else if (method === 'DELETE') {
      let itemId = this.extractIdFromUrl(url) || resolvedData.id;
      
      // If itemId is temporary, try to get real ID from mapping
      if (itemId && itemId.toString().startsWith('temp_')) {
        const realId = await idMapper.getRealId(itemId);
        if (realId) {
          itemId = realId;
        } else {
          // If temp ID not resolved, item was never synced, just delete locally
          await db.sync_queue.delete(item.id);
          return; // Skip server deletion
        }
      }
      
      if (!itemId) {
        throw new Error('Cannot sync: ID not found');
      }
      
      const urlWithId = resourceUrl(url, itemId);
      response = await api.delete(urlWithId);
    }

    if (response && response.status >= 200 && response.status < 300) {
      await this.markAsSynced(item, response.data);
      await db.sync_queue.delete(item.id);
    } else {
      throw new Error(`Sync failed: ${response?.status || 'No response'}`);
    }
  }

  // ##### MARK AS SYNCED - Update local item with server response
  async markAsSynced(item, serverData) {
    const storeName = getStoreName(item.url);
    if (!storeName) return;

    const store = db[storeName];
    const localItem = item.data;

    if (item.method === 'POST' && serverData) {
      const tempId = localItem.id;
      
      // Map temporary ID to real ID
      if (tempId && tempId.toString().startsWith('temp_')) {
        await idMapper.mapTempToReal(tempId, serverData.id, storeName);
        
        // Delete old item with temp ID
        await store.delete(tempId);
      }
      
      // Save with real ID from server
      await store.put({
        ...serverData,
        updated_at: serverData.updated_at || serverData.created_at,
        local_updated_at: null,
        synced: true
      });
      
      // Update any items that reference this temp ID
      await this.updateReferencesToTempId(tempId, serverData.id, storeName);
      
    } else if ((item.method === 'PUT' || item.method === 'PATCH') && serverData) {
      const itemId = localItem.id?.toString().startsWith('temp_') 
        ? serverData.id 
        : localItem.id;
      
      if (itemId) {
        // If was temporary, map it
        if (localItem.id && localItem.id.toString().startsWith('temp_')) {
          await idMapper.mapTempToReal(localItem.id, serverData.id, storeName);
          await this.updateReferencesToTempId(localItem.id, serverData.id, storeName);
        }
        
        await store.put({
          ...serverData,
          id: itemId,
          updated_at: serverData.updated_at || serverData.created_at,
          local_updated_at: null,
          synced: true
        });
      }
    } else if (item.method === 'DELETE') {
      const itemId = localItem.id?.toString().startsWith('temp_')
        ? null
        : localItem.id;
      if (itemId) {
        await store.delete(itemId);
      }
    }
  }

  // ##### UPDATE REFERENCES TO TEMP ID
  // When a temp ID is resolved, update all items that reference it
  async updateReferencesToTempId(tempId, realId, resolvedStoreName) {
    if (!tempId || !tempId.toString().startsWith('temp_')) return;
    
    // Find all stores that might reference this store
    for (const [storeName, fields] of Object.entries(RELATIONSHIP_FIELDS)) {
      // Check if any field references the resolved store
      const store = db[storeName];
      if (!store) continue;
      
      for (const field of fields) {
        // Get store name from field (e.g., 'cluster_id' -> 'notepad_clusters')
        const referencedStore = this.getStoreNameFromField(field);
        if (referencedStore === resolvedStoreName) {
          // Find all items in this store that reference the temp ID
          const items = await store
            .where(field)
            .equals(tempId)
            .toArray();
          
          // Update each item to use real ID
          for (const item of items) {
            await store.update(item.id, {
              [field]: realId,
              local_updated_at: new Date().toISOString(),
              synced: false
            });
            
            // Add to sync queue if not already there
            const url = this.getUrlForStore(storeName);
            const existingQueueItem = await db.sync_queue
              .where('url')
              .equals(`${url}${item.id}/`)
              .and(item => ['pending', 'retrying'].includes(item.status))
              .first();
            
            if (!existingQueueItem) {
              await db.sync_queue.add({
                method: 'PATCH',
                url: `${url}${item.id}/`,
                data: { ...item, [field]: realId },
                timestamp: Date.now(),
                retries: 0,
                status: 'pending',
                conflict_status: 'none'
              });
            }
          }
        }
      }
    }
  }

  // ##### HELPER: Get store name from relationship field
  getStoreNameFromField(field) {
    const fieldToStore = {
      'cluster_id': 'notepad_clusters',
      'todo_id': 'todo',
      'trip_id': 'trips',
      'account_id': 'flow_accounts',
      'project': 'planner_projects',
      'column': 'planner_project_columns'
    };
    return fieldToStore[field] || null;
  }

  // ##### HELPER: Get URL for store
  getUrlForStore(storeName) {
    // Reverse lookup: find endpoint for store name
    for (const [endpoint, mappedStoreName] of Object.entries(endpointToStoreMap)) {
      if (mappedStoreName === storeName) {
        return endpoint;
      }
    }
    
    // Fallback mapping (if not found in endpointMapper)
    const storeToUrl = {
      'notepad_clusters': routes_api.notepad_clusters,
      'notes': routes_api.notepad_notes,
      'todo': routes_api.planner_todo,
      'todo_item': routes_api.planner_todo_items,
      'trips': routes_api.travel_trips,
      'places': routes_api.travel_places,
      'flow_accounts': routes_api.finance_accounts,
      'flow_accounts_history': routes_api.finance_accounts_history,
      'flow_categories': routes_api.finance_flows_categories,
      'flows': routes_api.finance_flows,
      'budget_plans': routes_api.finance_budget_plans,
      'budget_plan_items': routes_api.finance_budget_plan_items,
      'health_records': routes_api.health_records
    };
    return storeToUrl[storeName] || '';
  }

  // ##### HANDLE SYNC ERROR - Retry with exponential backoff
  async handleSyncError(item, error) {
    const retries = (item.retries || 0) + 1;

    if (retries >= this.maxRetries) {
      await db.sync_queue.update(item.id, {
        status: 'failed',
        retries,
        error: error.message
      });
      return;
    }

    const delay = this.retryDelay * Math.pow(2, retries - 1);
    
    // Marcar como "retrying" para evitar que sync() principal tente novamente
    await db.sync_queue.update(item.id, {
      retries,
      lastError: error.message,
      status: 'retrying'
    });

    setTimeout(async () => {
      if (navigator.onLine) {
        // Buscar item atualizado antes de tentar
        const updatedItem = await db.sync_queue.get(item.id);
        if (!updatedItem || updatedItem.status !== 'retrying') {
          return; // Item foi removido ou já processado
        }
        
        // Voltar para pending antes de tentar
        await db.sync_queue.update(item.id, { status: 'pending' });
        
        try {
          await this.syncItem(updatedItem);
        } catch (retryError) {
          await this.handleSyncError(updatedItem, retryError);
        }
      }
    }, delay);
  }

  // ##### RESOLVE CONFLICT - Resolve conflict with chosen strategy
  async resolveConflictWithStrategy(itemId, strategy) {
    const conflict = this.pendingConflicts.get(itemId);
    if (!conflict) {
      throw new Error('Conflict not found');
    }

    const { item, localItem, serverItem, storeName } = conflict;
    const resolvedItem = conflictResolver.resolveConflict(localItem, serverItem, strategy);

    // Atualizar item local com dados resolvidos
    const store = db[storeName];
    await store.put({
      ...resolvedItem,
      updated_at: serverItem.updated_at,
      local_updated_at: null,
      synced: false
    });

    // Atualizar dados na fila com dados resolvidos
    await db.sync_queue.update(item.id, {
      conflict_status: 'resolved',
      data: resolvedItem,
      conflict_resolved_at: Date.now(),
      conflict_strategy: strategy
    });

    // Remover do mapa de conflitos pendentes
    this.pendingConflicts.delete(itemId);

    // Notificar listeners
    this.notifyListeners({
      conflictResolved: true,
      conflictItemId: itemId
    });

    // Continuar sync deste item
    await this.syncItem(item);
  }

  // ##### GET PENDING CONFLICTS
  async getPendingConflicts() {
    const conflicts = await db.sync_queue
      .where('conflict_status')
      .equals('detected')
      .toArray();
    
    return conflicts.map(item => ({
      id: item.id,
      name: this.getItemDisplayName(item.data, getStoreName(item.url)),
      url: item.url,
      method: item.method
    }));
  }

  // ##### GET CONFLICT DETAILS - Get full conflict data for modal
  async getConflictDetails(itemId) {
    const conflict = this.pendingConflicts.get(itemId);
    if (!conflict) {
      // Try to get from queue
      const queueItem = await db.sync_queue.get(itemId);
      if (!queueItem || queueItem.conflict_status !== 'detected') {
        return null;
      }
      
      // Reconstruct conflict from queue item
      const storeName = getStoreName(queueItem.url);
      if (!storeName) return null;
      
      const localItem = await db[storeName].get(queueItem.data.id);
      if (!localItem) return null;
      
      // Fetch server item
      const itemId = this.extractIdFromUrl(queueItem.url) || queueItem.data.id;
      const urlWithId = resourceUrl(queueItem.url, itemId);
      
      try {
        const serverResponse = await api.get(urlWithId);
        return {
          item: queueItem,
          localItem,
          serverItem: serverResponse.data,
          storeName
        };
      } catch (error) {
        console.error('Failed to fetch server item:', error);
        return null;
      }
    }
    
    return conflict;
  }

  // ##### GET ITEM DISPLAY NAME
  getItemDisplayName(item, storeName) {
    if (!item) return 'Unknown';
    
    if (storeName === 'flows') {
      return item.desc || `Flow ${item.id}`;
    } else if (storeName === 'flow_accounts') {
      return item.account_name || `Account ${item.id}`;
    } else if (storeName === 'flow_categories') {
      return item.categ_l0 || `Category ${item.id}`;
    } else if (storeName === 'budget_plans') {
      return item.date || `Budget Plan ${item.id}`;
    } else if (storeName === 'budget_plan_items') {
      return item.description || `Budget Item ${item.id}`;
    } else if (storeName === 'health_records') {
      return item.type || `Health Record ${item.id}`;
    } else if (storeName === 'trips') {
      return item.title || `Trip ${item.id}`;
    } else if (storeName === 'places') {
      return item.name || `Place ${item.id}`;
    } else if (storeName === 'tasks') {
      return item.title || `Task ${item.id}`;
    } else if (storeName === 'planner_projects' || storeName === 'planner_project_columns') {
      return item.name || `Item ${item.id}`;
    } else if (storeName === 'planner_project_cards') {
      return item.title || `Card ${item.id}`;
    }
    
    return `Item ${item.id}`;
  }

  // ##### GET QUEUE ITEMS - Get items by status
  async getQueueItems(status) {
    return await db.sync_queue
      .where('status')
      .equals(status)
      .sortBy('timestamp');
  }

  // ##### GET ALL QUEUE ITEMS - Get all items in queue (pending, retrying, failed)
  async getAllQueueItems() {
    return await db.sync_queue
      .toCollection()
      .filter(item => ['pending', 'retrying', 'failed'].includes(item.status))
      .sortBy('timestamp');
  }

  // ##### SYNC SINGLE QUEUE ITEM - Sync a specific item from queue
  async syncSingleQueueItem(itemId) {
    const item = await db.sync_queue.get(itemId);
    if (!item) {
      throw new Error('Item not found in queue');
    }

    if (item.status === 'failed' || item.status === 'retrying') {
      // Reset retries and status to pending
      await db.sync_queue.update(itemId, {
        status: 'pending',
        retries: 0,
        error: null,
        lastError: null
      });
    }

    // Get updated item
    const updatedItem = await db.sync_queue.get(itemId);
    
    try {
      await this.syncItem(updatedItem);
      
      // Notify listeners about updated counts after successful sync
      this.notifyListeners({
        pendingCount: await db.sync_queue.where('status').equals('pending').count(),
        retryingCount: await db.sync_queue.where('status').equals('retrying').count(),
        failedCount: await db.sync_queue.where('status').equals('failed').count()
      });
    } catch (error) {
      // If sync fails, handle error
      await this.handleSyncError(updatedItem, error);
      
      // Notify listeners about updated counts after error
      this.notifyListeners({
        pendingCount: await db.sync_queue.where('status').equals('pending').count(),
        retryingCount: await db.sync_queue.where('status').equals('retrying').count(),
        failedCount: await db.sync_queue.where('status').equals('failed').count()
      });
      
      throw error;
    }
  }

  // ##### DELETE QUEUE ITEM - Delete item from queue
  async deleteQueueItem(itemId) {
    await db.sync_queue.delete(itemId);
    this.pendingConflicts.delete(itemId);
    
    // Notify listeners about updated counts
    this.notifyListeners({
      pendingCount: await db.sync_queue.where('status').equals('pending').count(),
      retryingCount: await db.sync_queue.where('status').equals('retrying').count(),
      failedCount: await db.sync_queue.where('status').equals('failed').count()
    });
  }

  // ##### GET SYNC STATUS
  async getStatus() {
    const pendingCount = await db.sync_queue.where('status').equals('pending').count();
    const retryingCount = await db.sync_queue.where('status').equals('retrying').count();
    const failedCount = await db.sync_queue.where('status').equals('failed').count();
    const conflictCount = await db.sync_queue.where('conflict_status').equals('detected').count();
    
    return {
      isSyncing: this.isSyncing,
      isOnline: navigator.onLine,
      pendingCount,
      retryingCount,
      failedCount,
      conflictCount
    };
  }

  // ##### RETRY FAILED - Retry failed items
  async retryFailed() {
    const failedItems = await db.sync_queue
      .where('status')
      .equals('failed')
      .toArray();

    for (const item of failedItems) {
      await db.sync_queue.update(item.id, {
        status: 'pending',
        retries: 0
      });
    }

    await this.sync();
  }

  // ##### ADD LISTENER - Subscribe to sync events
  addListener(callback) {
    this.syncListeners.push(callback);
    return () => {
      this.syncListeners = this.syncListeners.filter(cb => cb !== callback);
    };
  }

  // ##### NOTIFY LISTENERS - Notify all listeners
  notifyListeners(status) {
    this.syncListeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('Sync listener error:', error);
      }
    });
  }

  // ##### HELPER: Extract ID from URL
  extractIdFromUrl(url) {
    if (!url) return null;
    const path = url.split('?')[0];
    const matches = path.match(/\/(\d+|temp_[A-Za-z0-9_]+)\/?$/);
    if (!matches) return null;
    const raw = matches[1];
    if (raw.startsWith('temp_')) return raw;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : raw;
  }
}

export const syncService = new SyncService();
export default syncService;
